import { useEffect, useState } from "react";
import api, { fmtDate, formatApiError } from "@/lib/api";
import { PageHeader, TypeBadge, Loading, EmptyState } from "@/components/Shared";
import { toast } from "sonner";
import { Newspaper, Plus, X } from "lucide-react";

const TYPES = ["circulaire", "communique", "note", "alerte", "reglementation", "info", "avis"];

export default function AdminNews() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "circulaire" });

  const load = () => api.get("/admin/news").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    try {
      await api.post("/admin/news", { ...form, date: new Date(form.date || Date.now()).toISOString() });
      toast.success("Actualité publiée");
      setOpen(false); setForm({ type: "circulaire" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (items === null) return <Loading />;
  const input = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow text-sm";

  return (
    <div>
      <PageHeader title="Actualités" subtitle="Publiez circulaires, communiqués et alertes officielles.">
        <button onClick={() => setOpen(true)} data-testid="add-news" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors duration-200"><Plus className="h-4 w-4" /> Publier</button>
      </PageHeader>

      {items.length === 0 ? <EmptyState title="Aucune actualité" icon={Newspaper} /> : (
        <div className="space-y-3">
          {items.map((n) => (
            <div key={n.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-4" data-testid={`admin-news-${n.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap"><TypeBadge type={n.type} /><span className="text-xs font-semibold text-slate-500">{n.organism}</span><span className="text-xs text-slate-400">· {fmtDate(n.date)}</span></div>
                <div className="mt-1.5 font-bold text-slate-900">{n.title}</div>
                <p className="text-sm text-slate-500 mt-0.5">{n.summary}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="news-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-lg text-slate-900">Nouvelle actualité</h3><button onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="space-y-3">
              <div><label className="text-sm font-semibold text-slate-700">Titre</label><input data-testid="news-title" onChange={set("title")} className={input} /></div>
              <div><label className="text-sm font-semibold text-slate-700">Résumé</label><textarea data-testid="news-summary" onChange={set("summary")} className={input + " h-20 py-2"} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-semibold text-slate-700">Type</label>
                  <select value={form.type} onChange={set("type")} className={input + " capitalize"} data-testid="news-type">{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
                <div><label className="text-sm font-semibold text-slate-700">Organisme</label><input data-testid="news-organism" onChange={set("organism")} className={input} placeholder="Ministère de la Santé" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-semibold text-slate-700">Date</label><input type="date" onChange={set("date")} className={input} /></div>
                <div><label className="text-sm font-semibold text-slate-700">Lien source</label><input onChange={set("source_url")} className={input} placeholder="https://..." /></div>
              </div>
            </div>
            <button onClick={save} data-testid="save-news" className="mt-5 w-full h-11 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors duration-200">Publier</button>
          </div>
        </div>
      )}
    </div>
  );
}
